/**
 * LandRecommendation - Land count recommendation display
 *
 * Shows recommended land count based on format and curve,
 * with mana dork/rock adjustment and warning indicators.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { LandRecommendation as LandRecommendationData } from '../../hooks/useManaBaseAnalysis';

export interface LandRecommendationProps {
  data: LandRecommendationData;
  /** Compact mode for left panel */
  compact?: boolean;
}

function LandRecommendationInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          The land recommendation calculates how many lands your deck needs based on your <strong>format</strong> and{' '}
          <strong>average mana value</strong> (CMC). Each format has a baseline land count that gets adjusted based on your deck&apos;s
          curve.
        </p>
      </InfoSection>

      <InfoSection title="How it works">
        <InfoList
          items={[
            'Commander decks start at 33 lands and go up for higher curves',
            'Standard/Pioneer decks start at 21 lands',
            'Modern decks start at 20 lands, Legacy at 18',
            'Higher average mana value = more lands needed',
            'Mana dorks and rocks count as 0.5 "effective lands" each',
          ]}
        />
      </InfoSection>

      <InfoSection title="How to use it">
        <p className="mb-2">The status box shows your current count vs the recommended amount:</p>
        <InfoList
          items={[
            '✓ Green: Your land count is close to optimal',
            '⚠️ Yellow: You have more lands than needed — consider cutting some for more spells',
            '🔴 Red: You need more lands — add lands or risk not hitting drops',
          ]}
        />
      </InfoSection>

      <InfoTip>
        If you have many mana dorks (like Llanowar Elves) or mana rocks (like Sol Ring), you can run slightly fewer lands. The
        &quot;Effective&quot; count shows your total mana sources.
      </InfoTip>
    </>
  );
}

export default function LandRecommendation({ data, compact = true }: LandRecommendationProps): ReactElement {
  const { recommended, current, difference, manaSourceCount, effectiveLandCount, isWarning, suggestion } = data;

  // Status styling
  const statusColor = isWarning
    ? difference > 0
      ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
      : 'text-red-400 border-red-500/30 bg-red-500/10'
    : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10';

  const statusIcon = isWarning ? '⚠️' : '✓';

  return (
    <div className="space-y-3">
      {/* Main recommendation box */}
      <div className={clsx('rounded-lg border p-3', statusColor)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">
              {statusIcon} {current} / {recommended} lands
            </span>
            <InfoButton title="Land Recommendations">
              <LandRecommendationInfo />
            </InfoButton>
          </div>
          {difference !== 0 && (
            <span className="text-xs opacity-80">
              {difference > 0 ? '+' : ''}
              {difference}
            </span>
          )}
        </div>
        {suggestion && <p className="mt-1 text-xs opacity-80">{suggestion}</p>}
      </div>

      {/* Mana sources breakdown */}
      {manaSourceCount > 0 && (
        <div className="rounded-lg bg-zinc-800/30 p-3">
          <div className="text-ash mb-2 text-xs font-medium tracking-wider uppercase">Mana Sources</div>
          <div className={clsx('grid gap-2', compact ? 'grid-cols-2' : 'grid-cols-3')}>
            <div className="text-center">
              <div className="text-silver text-lg font-semibold">{current}</div>
              <div className="text-ash text-[10px]">Lands</div>
            </div>
            <div className="text-center">
              <div className="text-silver text-lg font-semibold">{manaSourceCount}</div>
              <div className="text-ash text-[10px]">Dorks/Rocks</div>
            </div>
            {!compact && (
              <div className="text-center">
                <div className="text-silver text-lg font-semibold">{effectiveLandCount.toFixed(1)}</div>
                <div className="text-ash text-[10px]">Effective</div>
              </div>
            )}
          </div>
          {compact && (
            <div className="text-ash mt-2 text-center text-xs">
              Effective: <span className="text-silver">{effectiveLandCount.toFixed(1)}</span> sources
            </div>
          )}
        </div>
      )}
    </div>
  );
}
